import { resolveRalphsLocationId } from "./kroger";
import { DEFAULT_RALPHS_LOCATION_ID } from "../constants/RalphsStore";

/**
 * Local / cached product search helpers.
 * Prefer these over live Kroger calls while the user is typing or revisiting terms.
 */

const productSearchApiCache = new Map();
const PRODUCT_SEARCH_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const PRODUCT_SEARCH_CACHE_MAX = 60;
const PRODUCT_SEARCH_SESSION_KEY = "bellhopt_product_search_cache_v1";

export const LIVE_SEARCH_MIN_CHARS = 2;
export const LIVE_SEARCH_API_MIN_CHARS = 3;
export const LIVE_SEARCH_DEBOUNCE_MS = 500;

const productIdentity = (product) =>
  String(
    product?.upc ||
      product?.productId ||
      product?._id ||
      product?.items?.[0]?.upc ||
      ""
  ).trim();

const productSearchText = (product) =>
  String(
    product?.description ||
      product?.name ||
      product?.title ||
      product?.brand ||
      product?.items?.[0]?.description ||
      ""
  )
    .trim()
    .toLowerCase();

export const normalizeProductSearchTerm = (term) => {
  const raw = String(term || "").trim();
  if (!raw) return "";
  try {
    return decodeURIComponent(raw).trim().toLowerCase();
  } catch {
    return raw.split("%20").join(" ").trim().toLowerCase();
  }
};

export const getActiveStoreCacheId = () => {
  try {
    return resolveRalphsLocationId();
  } catch {
    // resolveRalphsLocationId has a hard fallback, but keep the cache helper
    // defensive if storage is unavailable.
    return DEFAULT_RALPHS_LOCATION_ID;
  }
};

export const getProductSearchCacheKey = (
  term,
  start = 0,
  limit = 48,
  companyName = ""
) => {
  const storeId = getActiveStoreCacheId(companyName);
  return [
    String(companyName || "").toLowerCase(),
    storeId,
    normalizeProductSearchTerm(term),
    String(start || 0),
    String(limit || 48),
  ].join("::");
};

const readSessionSearchCache = () => {
  try {
    const raw = sessionStorage.getItem(PRODUCT_SEARCH_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeSessionSearchCache = (cacheObject) => {
  try {
    sessionStorage.setItem(
      PRODUCT_SEARCH_SESSION_KEY,
      JSON.stringify(cacheObject)
    );
  } catch {
    // ignore quota / private mode errors
  }
};

export const readProductSearchCache = (cacheKey) => {
  const memoryEntry = productSearchApiCache.get(cacheKey);
  if (memoryEntry) {
    if (Date.now() - memoryEntry.ts > PRODUCT_SEARCH_CACHE_TTL_MS) {
      productSearchApiCache.delete(cacheKey);
    } else {
      return memoryEntry.payload;
    }
  }

  const sessionCache = readSessionSearchCache();
  const sessionEntry = sessionCache[cacheKey];
  if (!sessionEntry) return null;
  if (Date.now() - sessionEntry.ts > PRODUCT_SEARCH_CACHE_TTL_MS) {
    delete sessionCache[cacheKey];
    writeSessionSearchCache(sessionCache);
    return null;
  }

  productSearchApiCache.set(cacheKey, sessionEntry);
  return sessionEntry.payload;
};

export const writeProductSearchCache = (cacheKey, payload) => {
  if (!cacheKey || !payload) return;
  const entry = { ts: Date.now(), payload };
  productSearchApiCache.set(cacheKey, entry);

  if (productSearchApiCache.size > PRODUCT_SEARCH_CACHE_MAX) {
    const oldestKey = productSearchApiCache.keys().next().value;
    if (oldestKey) productSearchApiCache.delete(oldestKey);
  }

  const sessionCache = readSessionSearchCache();
  sessionCache[cacheKey] = entry;
  const keys = Object.keys(sessionCache);
  if (keys.length > PRODUCT_SEARCH_CACHE_MAX) {
    keys
      .sort((a, b) => (sessionCache[a].ts || 0) - (sessionCache[b].ts || 0))
      .slice(0, keys.length - PRODUCT_SEARCH_CACHE_MAX)
      .forEach((key) => {
        delete sessionCache[key];
      });
  }
  writeSessionSearchCache(sessionCache);
};

export const hasProductSearchCache = (params = {}) => {
  const cacheKey = getProductSearchCacheKey(
    params.term,
    params.start || 0,
    params.limit || 48,
    params.companyName || ""
  );
  return Boolean(readProductSearchCache(cacheKey));
};

/**
 * Flatten already-loaded category + search products from Redux (no API).
 */
export const collectCachedStorefrontProducts = ({
  companyName,
  categoryProductsState,
  searchProducts,
} = {}) => {
  const collected = [];
  const seen = new Set();

  const pushAll = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((product) => {
      const id = productIdentity(product);
      if (!id || seen.has(id)) return;
      seen.add(id);
      collected.push(product);
    });
  };

  if (companyName && categoryProductsState?.[companyName]) {
    Object.values(categoryProductsState[companyName]).forEach((category) => {
      pushAll(category?.products);
    });
  }

  pushAll(searchProducts);

  return collected;
};

/**
 * If the search term matches an already-loaded category shelf, reuse it.
 */
export const findLoadedCategoryProducts = ({
  companyName,
  categoryProductsState,
  term,
} = {}) => {
  const normalizedTerm = normalizeProductSearchTerm(term);
  if (!normalizedTerm || !companyName || !categoryProductsState?.[companyName]) {
    return null;
  }

  const companyState = categoryProductsState[companyName];
  const matchKey = Object.keys(companyState).find((categoryName) => {
    if (
      categoryName === "containersMap" ||
      categoryName === "containerShown" ||
      categoryName === "firstProductsLoaded" ||
      categoryName === "error"
    ) {
      return false;
    }
    return normalizeProductSearchTerm(categoryName) === normalizedTerm;
  });

  if (!matchKey) return null;
  const products = companyState[matchKey]?.products;
  if (!Array.isArray(products) || !products.length) return null;

  return {
    products,
    total: companyState[matchKey]?.total || products.length,
    categoryName: matchKey,
  };
};

export const filterProductsBySearchTerm = (products, term, limit = 48) => {
  const query = normalizeProductSearchTerm(term);
  if (!query || !Array.isArray(products) || !products.length) return [];

  const scored = [];
  products.forEach((product) => {
    const haystack = productSearchText(product);
    if (!haystack) return;
    const index = haystack.indexOf(query);
    if (index === -1) return;
    const starts = haystack.startsWith(query);
    scored.push({
      product,
      score: starts ? 0 : index + 1,
    });
  });

  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((entry) => entry.product);
};

export const clearProductSearchPersistence = () => {
  try {
    localStorage.removeItem("lastSearchTerm");
  } catch {
    // ignore
  }
};
